import React, { useEffect, useMemo, useState } from 'react';
import { Typography, Card, Table, DatePicker, Button, Row, Col, Space, Input, Tag, Modal, Tabs, Switch, message } from 'antd';
import dayjs from 'dayjs';
import { refreshMonitoring, getRecentEvents, getMessageDetails, getMomentDetails, getMemberCoverage, getMonitoringContext, getCompletionByMember } from '../services/monitoringService';
import massSendService from '../services/massSendService';
import api from '../services/api';
import weworkService from '../services/weworkService';

const { RangePicker } = DatePicker;
const { Title, Paragraph } = Typography;

// 将后端事件合并为任务行
const toTaskRow = (evt) => ({
  id: evt.id,
  // 对于 SINGLE/GROUP 使用后端的 senderUserid；MOMENT 不在这里绑定成员
  userid: evt.category === 'MOMENT' ? undefined : evt.senderUserid,
  category: evt.category, // SINGLE | GROUP | MOMENT
  sourceType: evt.sourceType,
  sourceId: evt.sourceId,
  externalId: evt.externalId, // msgid/jobid
  triggeredAt: evt.triggeredAt,
  targetCount: evt.targetCount,
  successCount: evt.successCount,
  failCount: evt.failCount,
  status: evt.status,
  extInfo: evt.extInfo
});

const Monitoring = () => {
  // 时间筛选
  const [range, setRange] = useState([dayjs().startOf('day'), dayjs()]);
  const [memberKeyword, setMemberKeyword] = useState('');
  const [keywordInput, setKeywordInput] = useState('');

  // 刷新数据（清空缓存并彻底重新加载）
  const onRefreshData = async () => {
    try {
      setLoading(true);
      setExecStatusMap({});
      setMessageDetailsCache({});
      setMomentDetailsCache({});
      setTaskNameMap({});
      setCustomerNameMap({});
      await loadEvents();
      // 重新加载群聊名称映射
      try {
        const groups = await weworkService.getAllGroups();
        const map = {};
        (groups || []).forEach((g) => { if (g.chatId) map[g.chatId] = g.name || g.weworkGroup?.name || '群聊'; });
        setChatNameMap(map);
      } catch (_) { }
      message.success('已刷新数据');
    } catch (e) {
      message.error(e?.message || '刷新失败');
    } finally {
      setLoading(false);
    }
  };

  // 手动拉取数据（触发后端同步，默认拉取明细，60 秒冷却）
  const [syncLoading, setSyncLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);
  const onSync = async () => {
    if (cooldown > 0) return;
    try {
      setSyncLoading(true);
      const start = range?.[0]?.toISOString();
      const end = range?.[1]?.toISOString();
      await refreshMonitoring(start, end, true);
      message.info('正在查询数据，10s 后刷新页面');
      setCooldown(60);
    } catch (e) {
      message.error(e?.message || '触发同步失败');
    } finally {
      setSyncLoading(false);
    }
  };

  const onSyncAndAutoRefresh = async () => {
    await onSync();
    setTimeout(() => {
      onRefreshData();
    }, 10000);
  };

  // 成员信息映射
  const [userMap, setUserMap] = useState({}); // userid -> { name, userid }
  const [corpId, setCorpId] = useState('');
  useEffect(() => {
    (async () => {
      try {
        const list = await weworkService.getAllEmployees();
        const map = {};
        (list || []).forEach((u) => {
          if (!u) return;
          map[u.userid] = { userid: u.userid, name: u.name };
        });
        setUserMap(map);
      } catch (_) { }
    })();
  }, []);
  useEffect(() => {
    getMonitoringContext().then((ctx) => setCorpId(ctx?.corpId || '')).catch(() => { });
  }, []);

  // 拉取事件（3类）并前端按时间窗过滤
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]); // 全部事件
  const [taskNameMap, setTaskNameMap] = useState({}); // mass-send taskId -> taskName
  const [chatNameMap, setChatNameMap] = useState({}); // chatId -> group name
  const [execStatusMap, setExecStatusMap] = useState({}); // key -> { executed, label }
  const [messageDetailsCache, setMessageDetailsCache] = useState({}); // msgid -> details
  const [momentDetailsCache, setMomentDetailsCache] = useState({}); // momentId -> details
  const loadEvents = async () => {
    try {
      setLoading(true);
      // 缩小一次性抓取的事件条数，避免前端负担过重，并传入时间范围提升准确性
      const DEFAULT_SIZE = 200;
      const start = range?.[0]?.toISOString();
      const end = range?.[1]?.toISOString();
      const [s, g, m] = await Promise.all([
        getRecentEvents({ category: 'SINGLE', page: 0, size: DEFAULT_SIZE, start, end }),
        getRecentEvents({ category: 'GROUP', page: 0, size: DEFAULT_SIZE, start, end }),
        getRecentEvents({ category: 'MOMENT', page: 0, size: DEFAULT_SIZE, start, end })
      ]);
      const all = [
        ...(s?.items || []).map(toTaskRow),
        ...(g?.items || []).map(toTaskRow),
        ...(m?.items || []).map(toTaskRow)
      ];
      // 仅设置事件列表；避免默认请求 /v1/mass-send/tasks/{id}。
      // 任务名称按需在打开明细时再获取，减少不必要请求。
      setEvents(all);
    } catch (e) {
      message.error(e?.message || '加载事件失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range?.[0], range?.[1]]);

  // 预加载群聊名称映射（用于群聊明细显示）
  useEffect(() => {
    (async () => {
      try {
        const groups = await weworkService.getAllGroups();
        const map = {};
        (groups || []).forEach((g) => { if (g.chatId) map[g.chatId] = g.name || g.weworkGroup?.name || '群聊'; });
        setChatNameMap(map);
      } catch (_) { }
    })();
  }, []);

  // 覆盖（成员维度）：按需加载（当前页面未使用，避免额外请求）
  // 如需启用，请将下方函数绑定到对应按钮或Tab切换事件
  const loadCoverageAll = async () => {
    if (!corpId) return;
    try {
      const start = range?.[0]?.toISOString();
      const end = range?.[1]?.toISOString();
      await Promise.all([
        getMemberCoverage({ corpId, channel: 'SINGLE', start, end, byMember: true }),
        getMemberCoverage({ corpId, channel: 'GROUP', start, end, byMember: true }),
        getMemberCoverage({ corpId, channel: 'MOMENT', start, end, byMember: true })
      ]);
    } catch (_) {}
  };

  // 成员执行状态工具函数（放在使用之前，避免初始化报错）
  const memberExecKey = (task, userId) => {
    if (!task) return '';
    if (task.category === 'MOMENT') {
      try {
        const ext = task.extInfo ? JSON.parse(task.extInfo) : {};
        const mId = ext?.moment_id || '';
        return mId ? `MOMENT:${mId}:${userId}` : '';
      } catch (_) { return ''; }
    }
    return task.externalId ? `${task.category}:${task.externalId}:${userId}` : '';
  };

  const ensureMemberExecStatus = async (task, userId) => {
    const k = memberExecKey(task, userId);
    if (!k || execStatusMap[k]) return;
    try {
      if (task.category === 'MOMENT') {
        let momentId = '';
        try {
          const ext = task.extInfo ? JSON.parse(task.extInfo) : {};
          momentId = ext?.moment_id || '';
        } catch (_) { return; }
        if (!momentId) return;
        let md = momentDetailsCache[momentId];
        if (!md) {
          md = await getMomentDetails(momentId);
          setMomentDetailsCache((prev) => ({ ...prev, [momentId]: md }));
        }
        const mt = (md?.memberTasks || []).find((x) => x.userId === userId);
        const executed = mt?.publishStatus === 1;
        const label = executed ? '已发送' : '未发送';
        setExecStatusMap((prev) => ({ ...prev, [k]: { executed, label } }));
      } else {
        const msgid = task.externalId;
        if (!msgid) return;
        let det = messageDetailsCache[msgid];
        if (!det) {
          det = await getMessageDetails(msgid);
          setMessageDetailsCache((prev) => ({ ...prev, [msgid]: det }));
        }
        const mt = (det?.memberTasks || []).find((x) => x.userId === userId);
        const executed = mt?.status === 2; // 2=已发送
        const label = executed ? '已发送' : '未发送';
        setExecStatusMap((prev) => ({ ...prev, [k]: { executed, label } }));
      }
    } catch (_) { }
  };

  // 过滤与分组（成员维度）
  const [onlyPending, setOnlyPending] = useState(false);
  // 成员分页（后端分页）与完成度
  const [memberPage, setMemberPage] = useState(1); // antd Table 为1基
  const [memberPageSize, setMemberPageSize] = useState(10);
  const [membersTotal, setMembersTotal] = useState(0);
  const [membersServerItems, setMembersServerItems] = useState([]); // 当前页：[{userid,total,executed,rate}]
  const [completionMap, setCompletionMap] = useState({}); // 当前页：userid -> { total, executed, rate }
  const [rateOrder, setRateOrder] = useState('asc');
  // 将事件按成员展开（尤其是朋友圈MOMENT：基于visible_range.sender_list.user_list拆分到成员）
  const expandEventsByMember = (list) => {
    const expanded = [];
    (list || []).forEach((e) => {
      if (e.category === 'MOMENT') {
        try {
          const ext = e.extInfo ? JSON.parse(e.extInfo) : {};
          const vr = ext?.visible_range;
          const sl = vr && typeof vr === 'object' ? vr.sender_list : null;
          const ul = sl && typeof sl === 'object' ? sl.user_list : null;
          if (Array.isArray(ul) && ul.length > 0) {
            ul.forEach((uid) => {
              if (uid) expanded.push({ ...e, userid: String(uid) });
            });
            return;
          }
        } catch (_) { /* ignore */ }
        // 若没有可见范围，暂不展开，避免归到UNKNOWN
      } else {
        // SINGLE/GROUP 保持原样（已有senderUserid）
        expanded.push(e);
      }
    });
    return expanded;
  };

  const filteredGrouped = useMemo(() => {
    const start = range?.[0];
    const end = range?.[1];
    const inWindow = (t) => {
      if (!t) return false;
      const dt = dayjs(t);
      return (!start || dt.isAfter(start) || dt.isSame(start)) && (!end || dt.isBefore(end) || dt.isSame(end));
    };

    const filtered = (events || []).filter((e) => inWindow(e.triggeredAt));
    const expanded = expandEventsByMember(filtered);
    // 汇总事件到 map
    const eventsByUser = new Map();
    expanded.forEach((e) => {
      const uid = e.userid || 'UNKNOWN';
      if (!eventsByUser.has(uid)) eventsByUser.set(uid, []);
      eventsByUser.get(uid).push(e);
    });

    const serverUserids = (membersServerItems || []).map(it => it.userid).filter(Boolean);
    const rows = serverUserids.map((uid) => {
      const list = (eventsByUser.get(uid) || []).slice().sort((a, b) => dayjs(b.triggeredAt).valueOf() - dayjs(a.triggeredAt).valueOf());
      const agg = completionMap[uid] || {};
      const total = typeof agg.total === 'number' ? agg.total : list.length;
      const sentTotal = typeof agg.executed === 'number' ? agg.executed : 0;
      const pendingTotal = Math.max(0, (total || 0) - (sentTotal || 0));
      const rate = total ? (sentTotal / total) : 0;
      return {
        key: uid,
        userid: uid,
        name: userMap[uid]?.name,
        tasks: list,
        total,
        sentTotal,
        pendingTotal,
        completionRate: rate
      };
    });

    return rows;
  }, [events, range, userMap, execStatusMap, completionMap, membersServerItems]);

  // 当前页成员数据
  const currentMemberRows = filteredGrouped; // 已由后端分页控制

  // 加载成员完成度（后端分页聚合）
  useEffect(() => {
    (async () => {
      if (!corpId) return;
      try {
        const start = range?.[0]?.toISOString();
        const end = range?.[1]?.toISOString();
        const resp = await getCompletionByMember({ corpId, start, end, order: rateOrder || 'desc', page: memberPage - 1, size: memberPageSize, keyword: memberKeyword, onlyPending });
        const map = {};
        (resp?.items || []).forEach((it) => {
          if (it?.userid) map[it.userid] = { total: it.total, executed: it.executed, rate: it.rate };
        });
        setCompletionMap(map);
        setMembersServerItems(resp?.items || []);
        setMembersTotal(resp?.total || 0);
      } catch (_) {
        setCompletionMap({});
        setMembersServerItems([]);
        setMembersTotal(0);
      }
    })();
  }, [corpId, range?.[0], range?.[1], memberPage, memberPageSize, rateOrder, memberKeyword, onlyPending]);

  // 过滤条件变化时重置到第一页
  useEffect(() => { setMemberPage(1); }, [memberKeyword, onlyPending, range?.[0], range?.[1]]);

  // 仅在展开成员行时，才去加载该成员下各任务的执行状态明细（延迟加载）
  const [expandedUserKeys, setExpandedUserKeys] = useState([]);
  useEffect(() => {
    const set = new Set(expandedUserKeys || []);
    if (set.size === 0) return;
    // 找到当前展开的成员行对应的任务，逐一懒加载状态
    currentMemberRows.forEach((row) => {
      if (!row || !row.userid || !set.has(row.userid)) return;
      (row.tasks || []).forEach((task) => ensureMemberExecStatus(task, row.userid));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedUserKeys, currentMemberRows]);

  // 翻页时收起展开行，避免跨页残留
  useEffect(() => { setExpandedUserKeys([]); }, [memberPage, memberPageSize]);

  // 明细弹窗
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTitle, setDetailTitle] = useState('');
  const [detailCategory, setDetailCategory] = useState(null);
  const [detailData, setDetailData] = useState({ memberTasks: [], customerDetails: [], groupDetails: [], visibleCustomers: [] });
  const [customerNameMap, setCustomerNameMap] = useState({}); // externalUserId -> name

  const resolveCustomerNames = async (ids = []) => {
    const uniq = Array.from(new Set(ids.filter(Boolean)));
    const pending = uniq.filter((id) => !customerNameMap[id]);
    if (pending.length === 0) return;
    try {
      const results = await Promise.allSettled(
        pending.map((extId) => api.get(`/customers/external/WEWORK/${encodeURIComponent(extId)}`).then((r) => ({ id: extId, name: r.data?.name })))
      );
      const newMap = { ...customerNameMap };
      results.forEach((res, idx) => {
        if (res.status === 'fulfilled') {
          const { id, name } = res.value || {};
          if (id) newMap[id] = name || '';
        } else {
          const id = pending[idx];
          if (id) newMap[id] = '';
        }
      });
      setCustomerNameMap(newMap);
    } catch (_) { }
  };

  const openDetail = async (task, userFilter) => {
    try {
      setDetailOpen(true);
      setDetailLoading(true);
      const titleUser = userMap[task.userid]?.name || task.userid || '未知成员';
      let title = `${titleUser} - ${channelLabel(task.category)} - ${dayjs(task.triggeredAt).format('YYYY-MM-DD HH:mm')}`;
      // 关联任务名称（优先用缓存，其次请求接口）
      if (task.sourceType && String(task.sourceType).includes('MASS_SEND') && task.sourceId) {
        const cached = taskNameMap[task.sourceId];
        if (cached) {
          title = `${cached} · ${title}`;
        } else {
          try {
            const taskDetail = await massSendService.getTaskById(task.sourceId);
            const taskName = taskDetail?.data?.taskName || taskDetail?.taskName;
            if (taskName) title = `${taskName} · ${title}`;
          } catch (_) { }
        }
      }
      setDetailTitle(title);
      setDetailCategory(task.category);
      if (task.category === 'MOMENT') {
        let momentId = '';
        try {
          const ext = task.extInfo ? JSON.parse(task.extInfo) : {};
          momentId = ext?.moment_id || '';
        } catch (_) { }
        if (!momentId) {
          message.info('该任务尚未回填momentId，无法查看明细');
          setDetailLoading(false);
          return;
        }
        const m = await getMomentDetails(momentId);
        // 仅展示点击成员自己的任务与可见客户
        const memberTasks = (m?.memberTasks || []).filter(it => !userFilter || it.userId === userFilter);
        const visibleCustomers = (m?.visibleCustomers || []).filter(it => !userFilter || it.userId === userFilter);
        setDetailData({ ...m, memberTasks, visibleCustomers });
        const vids = visibleCustomers.map((x) => x.externalUserId).filter(Boolean);
        await resolveCustomerNames(vids);
      } else {
        // SINGLE / GROUP
        const d = await getMessageDetails(task.externalId);
        // 仅展示点击成员自己的任务与明细
        const memberTasks = (d?.memberTasks || []).filter(it => !userFilter || it.userId === userFilter);
        const customerDetails = (d?.customerDetails || []).filter(it => !userFilter || it.userId === userFilter);
        const groupDetails = (d?.groupDetails || []).filter(it => !userFilter || it.userId === userFilter);
        setDetailData({ ...d, memberTasks, customerDetails, groupDetails });
        const cids = customerDetails.map((x) => x.externalUserId).filter(Boolean);
        await resolveCustomerNames(cids);
      }
    } catch (e) {
      message.error(e?.message || '加载明细失败');
    } finally {
      setDetailLoading(false);
    }
  };

  const memberColumns = [
    {
      title: '成员',
      dataIndex: 'userid',
      render: (v, r) => (
        <Space>
          <b>{r.name || '-'}</b>
          {/* 隐藏原始ID以降低干扰 */}
        </Space>
      )
    },
    { title: '总任务', dataIndex: 'total', width: 100 },
    { title: '已发送', dataIndex: 'sentTotal', width: 100 },
    { title: '待发送', dataIndex: 'pendingTotal', width: 100, render: (v) => <Tag color={v > 0 ? 'orange' : 'green'}>{v}</Tag> },
    {
      title: '完成率',
      dataIndex: 'completionRate',
      key: 'completionRate',
      width: 110,
      sorter: true,
      defaultSortOrder: 'ascend',
      render: (_, r) => {
        const rate = r.total ? (r.sentTotal / r.total) : 0;
        return (rate * 100).toFixed(1) + '%';
      }
    }
  ];

  // 列表排序联动后端（完成率）
  const handleTableChange = async (pagination, filters, sorter) => {
    // 分页
    if (pagination) {
      if (pagination.current && pagination.current !== memberPage) setMemberPage(pagination.current);
      if (pagination.pageSize && pagination.pageSize !== memberPageSize) setMemberPageSize(pagination.pageSize);
    }
    const field = sorter?.field || sorter?.columnKey;
    if (field === 'completionRate') {
      const ord = sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : null;
      if (!ord) {
        setRateOrder('desc');
        return;
      }
      setRateOrder(ord);
    } else {
      // 恢复默认排序
      setRateOrder('desc');
    }
  };

  // 取消默认按完成率排序的预加载，避免重复请求；仅在用户交互排序时加载

  const statusMap = { CREATED: '已创建', SENT: '已发送', SYNCED: '已同步', FAILED: '失败' };
  const channelLabel = (c) => (c === 'SINGLE' ? '客户群发' : c === 'GROUP' ? '群聊群发' : '朋友圈');
  const getTaskTitle = (row) => {
    if (!row) return '任务';
    // extInfo中的可能标题
    try {
      if (row.extInfo) {
        const ext = JSON.parse(row.extInfo);
        if (ext?.taskName) return ext.taskName;
        if (ext?.name) return ext.name;
        if (ext?.title) return ext.title;
      }
    } catch (_) { }
    // 群发任务名称
    if (row.sourceType && String(row.sourceType).includes('MASS_SEND') && row.sourceId && taskNameMap[row.sourceId]) {
      return taskNameMap[row.sourceId];
    }
    // 兜底
    return channelLabel(row.category) + '任务';
  };

  const taskColumns = [
    { title: '任务', dataIndex: 'title', width: 220, render: (_, row) => getTaskTitle(row) },
    { title: '时间', dataIndex: 'triggeredAt', render: (t) => dayjs(t).format('YYYY-MM-DD HH:mm:ss'), width: 200 },
    { title: '类型', dataIndex: 'category', width: 140, render: (c) => <Tag color={c === 'SINGLE' ? 'blue' : c === 'GROUP' ? 'purple' : 'green'}>{channelLabel(c)}</Tag> },
    { title: '状态', dataIndex: 'status', width: 140, render: (s) => statusMap[s] || s },
    { title: '操作', key: 'op', width: 140, render: (_, row) => (<Button size="small" onClick={() => openDetail(row)}>查看明细</Button>) }
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={5}>群发监控</Title>
          </Col>
        </Row>
      </div>

      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Row gutter={24} align="middle">
          <Col>
            <span>时间：</span>
            <RangePicker
              showTime
              value={range}
              onChange={setRange}
              presets={[
                { label: '今天', value: [dayjs().startOf('day'), dayjs().endOf('day')] },
                { label: '昨天', value: [dayjs().subtract(1, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day')] },
                { label: '近3天', value: [dayjs().subtract(2, 'day').startOf('day'), dayjs().endOf('day')] }
              ]}
            />
          </Col>
          <Col>
            <Input
              allowClear
              style={{ width: 260 }}
              placeholder="搜索成员姓名/账号"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onPressEnter={() => setMemberKeyword(keywordInput)}
            />
          </Col>
          <Col>
            <Button onClick={() => setMemberKeyword(keywordInput)} type="primary">查询</Button>
          </Col>
          <Col>
            <Space>
              <span>仅看待发送成员：</span>
              <Switch checked={onlyPending} onChange={setOnlyPending} />
            </Space>
          </Col>
        </Row>
          <Row gutter={24} align="middle">
            {/* <Col>
              <Button type="primary" onClick={onSyncAndAutoRefresh} loading={syncLoading || loading} disabled={cooldown > 0}>刷新</Button>
              {cooldown > 0 && (
                <span style={{ marginLeft: 8, color: '#999' }}>可在 {cooldown}s 后再次拉取</span>
              )}
            </Col> */}
          </Row>
        </Space>
        <Table
          rowKey={(r) => r.key}
          loading={loading}
          dataSource={currentMemberRows}
          columns={memberColumns}
          pagination={{ current: memberPage, pageSize: memberPageSize, total: membersTotal, showSizeChanger: true }}
          onChange={handleTableChange}
          expandable={{
            expandedRowKeys: expandedUserKeys,
            onExpand: (expanded, record) => {
              setExpandedUserKeys((prev) => {
                const set = new Set(prev || []);
                if (expanded) set.add(record.userid);
                else set.delete(record.userid);
                return Array.from(set);
              });
            },
            expandedRowRender: (record) => (
              <Table
                size="small"
                rowKey={(r) => `${record.userid}-${r.id}-${r.externalId}`}
                dataSource={record.tasks}
                columns={[
                  { title: '任务', dataIndex: 'title', width: 240, render: (_, row) => getTaskTitle(row) },
                  { title: '时间', dataIndex: 'triggeredAt', render: (t) => dayjs(t).format('YYYY-MM-DD HH:mm:ss'), width: 200 },
                  { title: '类型', dataIndex: 'category', width: 140, render: (c) => <span>{channelLabel(c)}</span> },
                  {
                    title: '状态', key: 'exec', width: 140, render: (_, row) => {
                      const k = memberExecKey(row, record.userid);
                      const label = k && execStatusMap[k]?.label;
                      return label || '加载中...';
                    }
                  },
                  {
                    title: '操作', key: 'op', width: 140, render: (_, row) => (
                      <Button type="link" size="small" onClick={() => openDetail(row, record.userid)}>查看明细</Button>
                    )
                  }
                ]}
                pagination={{ pageSize: 10 }}
              />
            )
          }}
        />
      </Card>

      <Modal
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        title={detailTitle}
        width={1000}
        footer={null}
      >
        {detailLoading ? (
          <div style={{ height: 240 }} />
        ) : (
          (() => {
            const items = [];
            // 成员任务tab：根据渠道展示不同字段
            if (detailCategory === 'MOMENT') {
              items.push({
                key: 'member',
                label: '成员任务',
                children: (
                  <Table
                    size="small"
                    rowKey={(r, i) => `m-${i}`}
                    dataSource={detailData?.memberTasks || []}
                    pagination={{ pageSize: 10 }}
                    columns={[
                      { title: '成员', dataIndex: 'userId', render: (v) => userMap[v]?.name || v },
                      { title: '状态', dataIndex: 'publishStatus', render: (v) => (v === 1 ? '已发表' : '未发表') },
                      { title: '时间', dataIndex: 'publishedAt', render: (t) => t ? dayjs(t).format('YYYY-MM-DD HH:mm:ss') : '-' }
                    ]}
                  />
                )
              });
            } else {
              items.push({
                key: 'member',
                label: '成员任务',
                children: (
                  <Table
                    size="small"
                    rowKey={(r, i) => `m-${i}`}
                    dataSource={detailData?.memberTasks || []}
                    pagination={{ pageSize: 10 }}
                    columns={[
                      { title: '成员', dataIndex: 'userId', render: (v) => userMap[v]?.name || v },
                      { title: '状态', dataIndex: 'status', render: (v) => {
                        if (v === 2) return '已发送';
                        if (v === 1) return '待发送';
                        if (v === 0) return '未发送';
                        return typeof v === 'number' ? `状态${v}` : (v || '-');
                      } },
                      { title: '时间', dataIndex: 'sendTime' }
                    ]}
                  />
                )
              });
            }
            if (detailCategory === 'SINGLE') {
              items.push({
                key: 'customer',
                label: '客户明细',
                children: (
                  <Table
                    size="small"
                    rowKey={(r, i) => `c-${i}`}
                    dataSource={detailData?.customerDetails || []}
                    pagination={{ pageSize: 10 }}
                    columns={[
                      { title: '成员', dataIndex: 'userId', render: (v) => userMap[v]?.name || v },
                      { title: '客户', dataIndex: 'externalUserId', render: (v) => (customerNameMap[v] || v || '-') },
                      { title: '状态', dataIndex: 'status', render: (v) => {
                        if (v === 1) return '已发送';
                        if (v === 0) return '未发送';
                        if (v === 2) return '不是好友';
                        if (v === 3) return '被其他群发占用';
                        return typeof v === 'number' ? `状态${v}` : (v || '-');
                      } },
                      { title: '时间', dataIndex: 'sendTime' }
                    ]}
                  />
                )
              });
            }
            if (detailCategory === 'GROUP') {
              items.push({
                key: 'group',
                label: '群聊明细',
                children: (
                  <Table
                    size="small"
                    rowKey={(r, i) => `g-${i}`}
                    dataSource={detailData?.groupDetails || []}
                    pagination={{ pageSize: 10 }}
                    columns={[
                      { title: '成员', dataIndex: 'userId', render: (v) => userMap[v]?.name || v },
                      { title: '群聊', dataIndex: 'chatId', render: (v) => (chatNameMap[v] || v || '-') },
                      { title: '状态', dataIndex: 'status', render: (v) => {
                        if (v === 1) return '已发送';
                        if (v === 0) return '未发送';
                        return typeof v === 'number' ? `状态${v}` : (v || '-');
                      } },
                      { title: '时间', dataIndex: 'sendTime' }
                    ]}
                  />
                )
              });
            }
            if (detailCategory === 'MOMENT') {
              items.push({
                key: 'moment',
                label: '朋友圈可见',
                children: (
                  <Table
                    size="small"
                    rowKey={(r, i) => `mv-${i}`}
                    dataSource={detailData?.visibleCustomers || []}
                    pagination={{ pageSize: 10 }}
                    columns={[
                      { title: '成员', dataIndex: 'userId', render: (v) => userMap[v]?.name || v },
                      { title: '客户', dataIndex: 'externalUserId', render: (v) => (customerNameMap[v] || v || '-') }
                    ]}
                  />
                )
              });
            }
            return <Tabs items={items} />;
          })()
        )}
      </Modal>
    </Space>
  );
};

export default Monitoring;
